// Front-end data service — talks to /api/data/* (Azure SQL behind Azure Functions).
// Replaces services/sharepoint.js. Same field names in and out, so pages don't change.
//
// Auth: we send the MSAL id_token as a bearer token. The server verifies it against
// Entra and decides what the caller may write — permissions are enforced server-side,
// not by hiding buttons.

const BASE = "/api/data";

async function call(path, { method = "GET", token, body } = {}) {
  const res = await fetch(`${BASE}/${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      // Static Web Apps overwrites the Authorization header with its own before
      // the request reaches a managed function (we measured our 1214-char token
      // arriving as a different 365-char one), so send ours in a custom header.
      ...(token ? { "X-Delphi-Token": token } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data;
  try { data = text ? JSON.parse(text) : null; } catch { throw new Error(`Server error: ${text.slice(0, 200)}`); }
  if (!res.ok) throw new Error((data && data.error) || `Request failed (HTTP ${res.status}).`);
  return data;
}

const list = (resource) => (token) => call(resource, { token });
const create = (resource) => (token, fields) => call(resource, { method: "POST", token, body: fields });
const update = (resource) => (token, id, fields) => call(`${resource}/${id}`, { method: "PATCH", token, body: fields });
const remove = (resource) => (token, id) => call(`${resource}/${id}`, { method: "DELETE", token });

// ─── People ───
export const getPeople = list("people");
export const createPerson = create("people");
export const updatePerson = update("people");
export const deletePerson = remove("people");

// ─── Projects ───
export const getProjects = list("projects");
export const createProject = create("projects");
export const updateProject = update("projects");
export const deleteProject = remove("projects");

// ─── Deleted projects (the recycle bin) ───
// Deleting a project is a soft delete: the row is stamped, its crew, runoff,
// narratives and budget snapshots are untouched, and restoring brings it all
// back. Finance and admins can see and restore.
export const getDeletedProjects = list("deletedProjects");
export const restoreProject = (token, id) => update("projects")(token, id, { Restore: true });

// ─── Team members (open seat = PersonName "(Open Seat)") ───
export const getTeamMembers = list("teamMembers");
export const createTeamMember = create("teamMembers");
export const updateTeamMember = update("teamMembers");
export const deleteTeamMember = remove("teamMembers");

// ─── Billable rates ───
export const getBillableRates = list("rates");
export const createRate = create("rates");
export const updateRate = update("rates");
export const deleteRate = remove("rates");

// ─── Runoff (Financials matrix) ───
export const getRunoffEntries = list("runoff");
export const createRunoffEntry = create("runoff");
export const updateRunoffEntry = update("runoff");
export const deleteRunoffEntry = remove("runoff");

// ─── Feedback (anyone signed in can submit; admins can read/triage) ───
export const getFeedback = list("feedback");
export const createFeedback = create("feedback");
export const updateFeedback = update("feedback");
export const deleteFeedback = remove("feedback");

// ─── Narratives ───
export async function getNarrative(token, jobNumber) {
  const all = await call("narratives", { token });
  return all.filter(n => n.ProjectJobNumber === jobNumber)
    .sort((a, b) => new Date(b.GeneratedDate || 0) - new Date(a.GeneratedDate || 0))[0] || null;
}
export const saveNarrative = create("narratives");

// ─── Budget snapshots ───
export async function getBudgetSnapshots(token, jobNumber) {
  const all = await call("budgetSnapshots", { token });
  return jobNumber ? all.filter(b => String(b.ProjectJobNumber) === String(jobNumber)) : all;
}
export const createBudgetSnapshotRow = create("budgetSnapshots");

